// vietnamV2Copy.ts
// ─── Astria Vietnam v2 — bilingual (EN/VI) UI copy ─────────────────────────
// All fixed UI strings for the shell header, tab bar, and the tab screen's
// chrome (loading/reset/submit/CTA text). The client-approved backend copy
// pack response text itself is NOT translated here — it always comes back
// in Vietnamese from helper/astriaVietnamV2Service.js regardless of this
// toggle, since that's the deterministic, client-approved output. This file
// only localizes the surrounding UI chrome.
import { VietnamV2Lang } from "./vietnamV2Language";
import { AstriaVietnamV2Tab } from "./AstriaVietnamV2Shell";

interface ShellCopy {
  title: string;
  subtitle: string;
  langToggleLabel: string;
}

interface TabBarCopy {
  label: string;
  sublabel: string;
}

interface TabScreenCopy {
  question: string;
  helper: string;
  fields: Record<string, { label: string; options?: Record<string, string>; placeholder?: string; lowLabel?: string; highLabel?: string }>;
}

interface VietnamV2CopyShape {
  shell: ShellCopy;
  tabBar: Record<AstriaVietnamV2Tab, TabBarCopy>;
  screens: Record<AstriaVietnamV2Tab, TabScreenCopy>;
  submitButton: string;
  loadingLabel: string;
  resetLink: string;
}

export const VIETNAM_V2_COPY: Record<VietnamV2Lang, VietnamV2CopyShape> = {
  vi: {
    shell: {
      title: "Astria Vietnam",
      subtitle: "Một khoảng lặng ấm áp để bạn nhìn lại cảm xúc của mình",
      langToggleLabel: "Ngôn ngữ",
    },
    tabBar: {
      soft_summary: { label: "Tổng Quan Nhẹ", sublabel: "Tổng Quan Nhẹ" },
      connection_atmosphere: { label: "Không Khí Kết Nối", sublabel: "Không Khí Kết Nối" },
      timing_rhythm: { label: "Nhịp Thời Điểm", sublabel: "Nhịp Thời Điểm" },
      emotion_balance: { label: "Cân Bằng Cảm Xúc", sublabel: "Cân Bằng Cảm Xúc" },
      guidance_path: { label: "Hướng Đi Nhẹ Nhàng", sublabel: "Hướng Đi Nhẹ Nhàng" },
      your_note: { label: "Ghi Chú Của Bạn", sublabel: "Ghi Chú Của Bạn" },
    },
    screens: {
      soft_summary: {
        question: "Bạn đang cảm thấy thế nào?",
        helper: "Một khoảng lặng nhẹ để bạn nhìn lại cảm xúc ngay lúc này.",
        fields: {
          mood: { label: "Cảm xúc" },
          intensity: {
            label: "Mức độ cảm xúc",
            lowLabel: "Nhẹ",
            highLabel: "Mạnh",
          },
        },
      },
      connection_atmosphere: {
        question: "Không khí giữa hai bạn đang thế nào?",
        helper: "Sự ấm áp (hay khoảng cách) bạn đang cảm nhận với người ấy.",
        fields: {
          connection: {
            label: "Không khí giữa hai bạn",
            options: {
              warm: "Ấm áp",
              distant: "Xa cách",
              tense: "Căng thẳng",
              playful: "Vui vẻ",
              quiet: "Lặng lẽ",
            },
          },
          tone: {
            label: "Giọng điệu của họ",
            options: {
              gentle: "Nhẹ nhàng",
              guarded: "Dè dặt",
              open: "Cởi mở",
              unsure: "Chưa rõ",
            },
          },
        },
      },
      timing_rhythm: {
        question: "Nhịp hiện tại của hai bạn ra sao?",
        helper: "Nhịp độ và mức độ sẵn sàng của thời điểm này.",
        fields: {
          flow: {
            label: "Nhịp hiện tại",
            options: {
              slow: "Chậm",
              steady: "Ổn định",
              rushed: "Vội vàng",
              uncertain: "Chưa chắc",
            },
          },
          readiness: {
            label: "Mức độ sẵn sàng",
            lowLabel: "Chưa sẵn sàng",
            highLabel: "Đã sẵn sàng",
          },
        },
      },
      emotion_balance: {
        question: "Vùng cảm xúc của bạn đang ở đâu?",
        helper: "Nơi cảm xúc của bạn đang lắng lại ngay lúc này.",
        fields: {
          emotion_zone: {
            label: "Vùng cảm xúc",
            options: {
              warm: "Ấm",
              heavy: "Nặng",
              light: "Nhẹ",
              tender: "Dịu",
              steady: "Ổn định",
            },
          },
          location: {
            label: "Cảm xúc nằm ở đâu?",
            options: {
              chest: "Lồng ngực",
              mind: "Tâm trí",
              stomach: "Dạ dày",
              everywhere: "Khắp người",
            },
          },
        },
      },
      guidance_path: {
        question: "Điều gì đang xảy ra?",
        helper: "Một hướng đi nhẹ nhàng cho điều bạn đang đối mặt.",
        fields: {
          issue: {
            label: "Điều đang xảy ra",
            options: {
              communication: "Giao tiếp",
              distance: "Khoảng cách",
              uncertainty: "Chưa chắc chắn",
              overwhelm: "Quá tải",
            },
          },
          desired: {
            label: "Bạn muốn điều gì?",
            options: {
              clarity: "Rõ ràng",
              closeness: "Gần gũi",
              peace: "Bình yên",
              courage: "Can đảm",
            },
          },
        },
      },
      your_note: {
        question: "Bạn đang nghĩ gì?",
        helper: "Một lời phản hồi nhẹ nhàng cho tin nhắn gần nhất của bạn.",
        fields: {
          latest_message: {
            label: "Tin nhắn gần nhất của bạn",
            placeholder: "Bạn muốn nói điều gì?",
          },
          feeling: {
            label: "Bạn cảm thấy thế nào?",
            options: {
              soft: "Mềm mại",
              hopeful: "Hy vọng",
              unsure: "Chưa chắc",
              relieved: "Nhẹ nhõm",
            },
          },
        },
      },
    },
    submitButton: "Xem kết quả",
    loadingLabel: "Đang lắng nghe cảm xúc của bạn…",
    resetLink: "Bắt đầu lượt check-in mới",
  },
  en: {
    shell: {
      title: "Astria Vietnam",
      subtitle: "A warm, gentle space to check in with how you're feeling",
      langToggleLabel: "Language",
    },
    tabBar: {
      soft_summary: { label: "Soft Summary", sublabel: "Tổng Quan Nhẹ" },
      connection_atmosphere: { label: "Connection Atmosphere", sublabel: "Không Khí Kết Nối" },
      timing_rhythm: { label: "Timing Rhythm", sublabel: "Nhịp Thời Điểm" },
      emotion_balance: { label: "Emotion Balance", sublabel: "Cân Bằng Cảm Xúc" },
      guidance_path: { label: "Guidance Path", sublabel: "Hướng Đi Nhẹ Nhàng" },
      your_note: { label: "Your Note", sublabel: "Ghi Chú Của Bạn" },
    },
    screens: {
      soft_summary: {
        question: "How are you feeling right now?",
        helper: "A gentle check-in on how you're feeling right now.",
        fields: {
          mood: { label: "Mood" },
          intensity: {
            label: "How strong is that feeling?",
            lowLabel: "Light",
            highLabel: "Strong",
          },
        },
      },
      connection_atmosphere: {
        question: "What's the atmosphere between you two?",
        helper: "The warmth (or distance) you're feeling with someone else.",
        fields: {
          connection: {
            label: "Atmosphere between you two",
            options: {
              warm: "Warm",
              distant: "Distant",
              tense: "Tense",
              playful: "Playful",
              quiet: "Quiet",
            },
          },
          tone: {
            label: "Their tone",
            options: {
              gentle: "Gentle",
              guarded: "Guarded",
              open: "Open",
              unsure: "Unsure",
            },
          },
        },
      },
      timing_rhythm: {
        question: "What's the rhythm of this moment?",
        helper: "The pace and readiness of this moment.",
        fields: {
          flow: {
            label: "Current flow",
            options: {
              slow: "Slow",
              steady: "Steady",
              rushed: "Rushed",
              uncertain: "Uncertain",
            },
          },
          readiness: {
            label: "How ready do you feel?",
            lowLabel: "Not yet",
            highLabel: "Ready",
          },
        },
      },
      emotion_balance: {
        question: "Where are your emotions sitting?",
        helper: "Where your emotions are sitting right now.",
        fields: {
          emotion_zone: {
            label: "Emotion zone",
            options: {
              warm: "Warm",
              heavy: "Heavy",
              light: "Light",
              tender: "Tender",
              steady: "Steady",
            },
          },
          location: {
            label: "Where does it show up most?",
            options: {
              chest: "Chest",
              mind: "Mind",
              stomach: "Stomach",
              everywhere: "Everywhere",
            },
          },
        },
      },
      guidance_path: {
        question: "What's going on?",
        helper: "A soft next step for what you're facing.",
        fields: {
          issue: {
            label: "What's going on",
            options: {
              communication: "Communication",
              distance: "Distance",
              uncertainty: "Uncertainty",
              overwhelm: "Overwhelm",
            },
          },
          desired: {
            label: "What do you want?",
            options: {
              clarity: "Clarity",
              closeness: "Closeness",
              peace: "Peace",
              courage: "Courage",
            },
          },
        },
      },
      your_note: {
        question: "What's on your mind?",
        helper: "A gentle reflection on your latest message.",
        fields: {
          latest_message: {
            label: "Your latest message",
            placeholder: "What would you like to say?",
          },
          feeling: {
            label: "How did you feel while writing it?",
            options: {
              soft: "Soft",
              hopeful: "Hopeful",
              unsure: "Unsure",
              relieved: "Relieved",
            },
          },
        },
      },
    },
    submitButton: "Reveal",
    loadingLabel: "Reading your check-in…",
    resetLink: "Start a new check-in",
  },
};
